import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { employeeApi, offboardingApi, docusignApi, extractErrorMessage } from '../../../services/hrApi';
import type { Employee } from '../../../types';
import './StepStyles.scss';

interface DocumentPackageStepProps {
  employeeId?: string;
  data?: any;
  onDataChange: (data: any) => void;
  onComplete: (isComplete: boolean) => void;
}

interface PackageDocument {
  id?: string;
  name: string;
  type: 'termination_letter' | 'additional_offboarding';
  fileKey?: string;
  fileUrl?: string;
  size?: number;
  uploadedAt?: string;
}

const DocumentPackageStep: React.FC<DocumentPackageStepProps> = ({
  employeeId,
  data,
  onDataChange,
  onComplete,
}) => {
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [creatingEnvelope, setCreatingEnvelope] = useState(false);

  // Package documents: Termination Letter and additional documents
  const [packageDocuments, setPackageDocuments] = useState<PackageDocument[]>([]);
  const [additionalDocuments, setAdditionalDocuments] = useState<PackageDocument[]>([]);

  // Track which documents to include in package
  const [documentsForSigning, setDocumentsForSigning] = useState<Set<string>>(
    new Set(data?.documentsForSigning || [])
  );

  // DocuSign modal state
  const [showDocuSignModal, setShowDocuSignModal] = useState(false);
  const [docusignConnected, setDocusignConnected] = useState(false);
  const [docusignLoading, setDocusignLoading] = useState(false);
  const [showEnvelopeConfirmModal, setShowEnvelopeConfirmModal] = useState(false);
  const [existingEnvelope, setExistingEnvelope] = useState<any>(null);
  const [checkingEnvelope, setCheckingEnvelope] = useState(false);
  const [checkingTabs, setCheckingTabs] = useState(false);
  const [openingSenderView, setOpeningSenderView] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  
  // Process flow states
  const [step1Completed, setStep1Completed] = useState(false); // Envelope prepared
  const [step2Completed, setStep2Completed] = useState(false); // Signature tabs placed

  const [formData, setFormData] = useState({
    recipient_email: data?.recipient_email || '',
    package_sent: data?.package_sent || false,
    sent_date: data?.sent_date || '',
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const formDataRef = useRef(formData);
  const lastCheckTimeRef = useRef<number>(0);
  const CHECK_INTERVAL = 1000; // Minimum 1 second between checks

  // Keep formDataRef in sync with formData
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    if (employeeId) {
      fetchEmployee();
      fetchDocuments();
    }
  }, [employeeId]);

  // Close actions menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showActionsMenu && !target.closest('.docusign-actions-menu')) {
        setShowActionsMenu(false);
      }
    };

    if (showActionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showActionsMenu]);

  useEffect(() => {
    // Auto-fill email from employee data if not set
    if (employee && !formData.recipient_email) {
      const updatedFormData = { ...formData, recipient_email: employee.email };
      setFormData(updatedFormData);
      // Defer callback to avoid calling during render
      const timeoutId = setTimeout(() => {
        onDataChange(updatedFormData);
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [employee]);

  useEffect(() => {
    // Restore documents for signing from saved data
    if (data?.documentsForSigning && Array.isArray(data.documentsForSigning)) {
      setDocumentsForSigning(new Set(data.documentsForSigning));
    }
  }, [data]);

  const checkDocuSignStatus = useCallback(async () => {
    setDocusignLoading(true);
    try {
      const status = await docusignApi.checkStatus();
      setDocusignConnected((status.connected && status.valid) || false);
    } catch (error) {
      console.error('Error checking DocuSign status:', error);
      setDocusignConnected(false);
    } finally {
      setDocusignLoading(false);
    }
  }, []);

  // Check DocuSign status when page loads
  useEffect(() => {
    checkDocuSignStatus();
  }, [checkDocuSignStatus]);

  // Check for existing envelope draft when employeeId or docusignConnected changes
  useEffect(() => {
    if (employeeId && docusignConnected) {
      const now = Date.now();
      // Only check if enough time has passed since last check (prevents duplicate calls)
      if (now - lastCheckTimeRef.current > CHECK_INTERVAL) {
        lastCheckTimeRef.current = now;
        checkExistingEnvelope();
      }
    }
  }, [employeeId, docusignConnected]);

  // Also check when component mounts or when data prop changes (step might be opened)
  useEffect(() => {
    if (employeeId && docusignConnected) {
      const now = Date.now();
      // Check if we should refresh (either first time or enough time has passed)
      if (lastCheckTimeRef.current === 0 || now - lastCheckTimeRef.current > CHECK_INTERVAL) {
        lastCheckTimeRef.current = now;
        // Small delay to ensure other effects complete first
        const timer = setTimeout(() => {
          checkExistingEnvelope();
        }, 200);
        return () => clearTimeout(timer);
      }
    }
  }, [employeeId]);

  const checkExistingEnvelope = async () => {
    if (!employeeId) return;
    setCheckingEnvelope(true);
    try {
      const result = await docusignApi.getEnvelopeDraft(employeeId as string, 'employee-offboarding');
      if (result.exists) {
        setExistingEnvelope(result);
        setStep1Completed(true); // Envelope exists, step 1 is complete
        
        // Check envelope status and update step completion
        const envelopeStatus = result.status || 'created';
        if (envelopeStatus === 'sent' || envelopeStatus === 'completed') {
          setStep2Completed(true); // Tabs were placed and envelope was sent/completed
          handleChange('package_sent', true); // Mark package as sent
        } else if (envelopeStatus === 'tabs_placed') {
          setStep2Completed(true); // Tabs were placed
          handleChange('package_sent', false); // Not sent yet
        } else if (envelopeStatus === 'created') {
          // Check if tabs were placed by querying DocuSign
          setCheckingTabs(true);
          try {
            const tabsResult = await docusignApi.checkEnvelopeTabs(result.envelope_id);
            if (tabsResult.success && tabsResult.has_tabs) {
              setStep2Completed(true);
              handleChange('package_sent', false); // Tabs placed but not sent
            } else {
              setStep2Completed(false);
              handleChange('package_sent', false);
            }
          } catch (error) {
            console.error('Error checking envelope tabs:', error);
            setStep2Completed(false);
            handleChange('package_sent', false);
          } finally {
            setCheckingTabs(false);
          }
        } else {
          setStep2Completed(false);
          handleChange('package_sent', false);
        }
      } else {
        setExistingEnvelope(null);
        setStep1Completed(false);
        setStep2Completed(false);
        handleChange('package_sent', false); // No envelope, package not sent
      }
    } catch (error) {
      console.error('Error checking existing envelope:', error);
      setExistingEnvelope(null);
      setStep1Completed(false);
      setStep2Completed(false);
      handleChange('package_sent', false);
    } finally {
      setCheckingEnvelope(false);
    }
  };

  const handleDocuSignLogin = async () => {
    try {
      setDocusignLoading(true);
      const response = await docusignApi.connect();

      if (response.success && response.auth_url) {
        if (response.code_verifier) {
          sessionStorage.setItem('docusign_code_verifier', response.code_verifier);
        }
        window.open(response.auth_url, "_blank", "width=600,height=700");
      } else {
        toast.error('Failed to get DocuSign authorization URL. Please try again.');
      }
    } catch (error) {
      console.error('Error initiating DocuSign login:', error);
      toast.error('Failed to initiate DocuSign login. Please try again.');
    } finally {
      setDocusignLoading(false);
    }
  };

  // Listen for DocuSign auth success message
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data === "docusign-auth-success") {
        checkDocuSignStatus().then(() => {
          // Check for envelope after connection
          if (employeeId) {
            checkExistingEnvelope();
          }
        });
      } else if (event.data === "docusign-auth-error") {
        toast.error('Failed to connect to DocuSign. Please try again.');
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [checkDocuSignStatus, employeeId]);

  const prevCompletionRef = useRef<boolean | null>(null);

  useEffect(() => {
    // Step is complete when package is sent
    const isComplete = formData.package_sent && (packageDocuments.length > 0 || additionalDocuments.length > 0);

    // Only call onComplete if the value actually changed
    if (prevCompletionRef.current !== isComplete) {
      prevCompletionRef.current = isComplete;
      // Defer callback to avoid calling during render
      const timeoutId = setTimeout(() => {
        onComplete(isComplete);
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [formData.package_sent, packageDocuments.length, additionalDocuments.length, onComplete]);

  const fetchEmployee = async () => {
    if (!employeeId) return;
    try {
      const emp = await employeeApi.getEmployee(employeeId);
      setEmployee(emp);
    } catch (error) {
      console.error('Error fetching employee:', error);
    }
  };

  const fetchDocuments = async () => {
    if (!employeeId) return;
    try {
      const docs = await employeeApi.getEmployeeDocuments(employeeId);
      const terminationLetter = docs.find((doc: any) => doc.type === 'termination_letter');
      const others = docs.filter((doc: any) => doc.type === 'additional_offboarding');

      const packageDocs: PackageDocument[] = [];
      if (terminationLetter) {
        // Get download URL for termination letter
        let fileUrl = '';
        try {
          const urlResult = await offboardingApi.getDocumentDownloadUrl(terminationLetter.path, 3600);
          fileUrl = urlResult.fileUrl || '';
        } catch (error) {
          console.error('Error getting termination letter URL:', error);
        }

        packageDocs.push({
          id: terminationLetter.id,
          name: terminationLetter.name,
          type: 'termination_letter',
          fileKey: terminationLetter.path,
          fileUrl: fileUrl,
          size: terminationLetter.size,
          uploadedAt: terminationLetter.updated_at || terminationLetter.created_at,
        });
      }

      setPackageDocuments(packageDocs);
      setAdditionalDocuments(
        others.map((doc: any) => ({
          id: doc.id,
          name: doc.name,
          type: 'additional_offboarding',
          fileKey: doc.path,
          size: doc.size,
          uploadedAt: doc.updated_at || doc.created_at,
        }))
      );
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const file = e.target.files?.[0];
    if (!file || !employeeId) return;

    const envelopeStatus = existingEnvelope?.status;
    
    // Flow 1: No envelope or voided envelope - proceed with upload
    if (!existingEnvelope || envelopeStatus === 'voided') {
      // No action needed, proceed with upload
    }
    // Flow 2: Draft status (created or tabs_placed) - warn and ask if draft should be deleted
    else if (envelopeStatus === 'created' || envelopeStatus === 'tabs_placed') {
      if (!window.confirm(`An envelope draft exists (status: ${envelopeStatus === 'created' ? 'Created' : 'Tabs Placed'}). Adding a new document will require deleting the existing draft. Do you want to continue?`)) {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      // Delete existing draft - only proceed if successful
      const draftDeleted = await handleDeleteDraft();
      if (!draftDeleted) {
        // Envelope deletion failed, don't proceed with file upload
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
    }
    // Flow 3: Sent or completed - ask if envelope should be voided
    else if (envelopeStatus === 'sent' || envelopeStatus === 'completed') {
      if (!window.confirm(`An envelope has been ${envelopeStatus === 'sent' ? 'sent' : 'completed'}. To add a new document, the envelope must be voided. Do you want to void the envelope?`)) {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      // Void the envelope - only proceed if successful
      setCreatingEnvelope(true);
      try {
        const result = await docusignApi.voidEnvelope(existingEnvelope.envelope_id);
        if (!result.success) {
          toast.error(`Failed to void envelope: ${result.error || 'Unknown error'}`);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          setCreatingEnvelope(false);
          return;
        }
        // Clear existing envelope state after voiding
        setExistingEnvelope(null);
        setStep1Completed(false);
        setStep2Completed(false);
        handleChange('package_sent', false);
        await checkExistingEnvelope();
      } catch (error: any) {
        console.error('Error voiding envelope:', error);
        const errorMessage = extractErrorMessage(error);
        toast.error(`Failed to void envelope: ${errorMessage}`);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setCreatingEnvelope(false);
        return;
      } finally {
        setCreatingEnvelope(false);
      }
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('employee_id', employeeId);
      formData.append('document_type', 'additional_offboarding');
      formData.append('name', file.name);

      // Get S3 path - use same structure as termination letters
      const userdata = localStorage.getItem('userdata');
      let userId = 'unknown';
      if (userdata) {
        try {
          const user = JSON.parse(userdata);
          userId = user.id || user.user_id || user.uid || 'unknown';
        } catch (e) {
          console.warn('Failed to parse userdata:', e);
        }
      }

      // Fallback to authUser if userdata doesn't have id
      if (userId === 'unknown') {
        const authUser = localStorage.getItem('authUser');
        if (authUser) {
          userId = authUser;
        }
      }

      // Use same path structure: "HR Portal/{userId}/Offboarding Documents/{employeeId}/{fileName}"
      const s3Path = `HR Portal/${userId}/Offboarding Documents/${employeeId}/${file.name}`;
      formData.append('s3_path', s3Path);

      const result = await offboardingApi.uploadDocument(formData);

      const newDoc: PackageDocument = {
        id: result.documentId,
        name: result.fileName || file.name,
        type: 'additional_offboarding',
        fileKey: result.fileKey,
        size: file.size,
        uploadedAt: new Date().toISOString(),
      };

      setAdditionalDocuments((prev) => [...prev, newDoc]);
      toast.success('Document uploaded successfully!');
      // Refresh envelope status after upload
      if (docusignConnected) {
        await checkExistingEnvelope();
      }
    } catch (error: any) {
      console.error('Error uploading document:', error);
      const errorMessage = error.response?.data?.error || error.response?.error || error.message || 'Upload failed';
      toast.error(`Failed to upload document: ${errorMessage}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveDocument = async (doc: PackageDocument) => {
    const envelopeStatus = existingEnvelope?.status;
    
    // Flow 1: No envelope or voided envelope - delete file directly
    if (!existingEnvelope || envelopeStatus === 'voided') {
      if (!doc.id || !window.confirm(`Are you sure you want to remove "${doc.name}" from the package?`)) {
        return;
      }
    }
    // Flow 2: Draft status (created or tabs_placed) - warn and ask if draft should be deleted
    else if (envelopeStatus === 'created' || envelopeStatus === 'tabs_placed') {
      if (!window.confirm(`An envelope draft exists (status: ${envelopeStatus === 'created' ? 'Created' : 'Tabs Placed'}). Removing this document will require deleting the existing draft. Are you sure you want to remove "${doc.name}"?`)) {
        return;
      }
      // Delete existing draft - only proceed if successful
      const draftDeleted = await handleDeleteDraft();
      if (!draftDeleted) {
        // Envelope deletion failed, don't proceed with document deletion
        return;
      }
    }
    // Flow 3: Sent or completed - ask if envelope should be voided
    else if (envelopeStatus === 'sent' || envelopeStatus === 'completed') {
      if (!window.confirm(`This document is part of an envelope that has been ${envelopeStatus === 'sent' ? 'sent' : 'completed'}. To remove this document, the envelope must be voided. Do you want to void the envelope?`)) {
        return;
      }
      // Void the envelope - only proceed if successful
      setCreatingEnvelope(true);
      try {
        const result = await docusignApi.voidEnvelope(existingEnvelope.envelope_id);
        if (!result.success) {
          toast.error(`Failed to void envelope: ${result.error || 'Unknown error'}`);
          setCreatingEnvelope(false);
          return;
        }
        // Clear existing envelope state after voiding
        setExistingEnvelope(null);
        setStep1Completed(false);
        setStep2Completed(false);
        handleChange('package_sent', false);
        await checkExistingEnvelope();
      } catch (error: any) {
        console.error('Error voiding envelope:', error);
        const errorMessage = extractErrorMessage(error);
        toast.error(`Failed to void envelope: ${errorMessage}`);
        setCreatingEnvelope(false);
        return;
      } finally {
        setCreatingEnvelope(false);
      }
    }

    // Proceed with document deletion
    try {
      // Delete from backend (database and S3)
      if (!doc.id) {
        toast.error('Document ID not found');
        return;
      }
      await employeeApi.deleteEmployeeDocument(doc.id);

      // Remove from local state
      setAdditionalDocuments((prev) => prev.filter((d) => d.id !== doc.id));

      // Remove from signing list if it was selected
      const docId = getDocumentId(doc);
      setDocumentsForSigning((prev) => {
        const updated = new Set(prev);
        updated.delete(docId);

        // Update form data
        const updatedFormData = {
          ...formData,
          documentsForSigning: Array.from(updated),
        };
        onDataChange(updatedFormData);

        return updated;
      });

      toast.success('Document removed successfully');
    } catch (error: any) {
      console.error('Error removing document:', error);
      const errorMessage = error.response?.data?.error || error.response?.error || error.message || 'Delete failed';
      toast.error(`Failed to remove document: ${errorMessage}`);
    }
  };

  const handlePrepareEnvelopeClick = () => {
    // Show confirmation modal
    setShowEnvelopeConfirmModal(true);
  };

  const handleConfirmPrepareEnvelope = async () => {
    setShowEnvelopeConfirmModal(false);
    
    if (!employeeId || !employee) {
      toast.error('Employee information is required');
      return;
    }

    const allDocuments = [...packageDocuments, ...additionalDocuments];
    if (allDocuments.length === 0) {
      toast.error('No documents in package. Please add documents first.');
      return;
    }

    // Get all document IDs (from database records)
    const documentIds = allDocuments
      .map((doc) => doc.id)
      .filter((id): id is string => !!id);

    if (documentIds.length === 0) {
      toast.error('No valid document IDs found. Documents must be saved to the database first.');
      return;
    }

    setCreatingEnvelope(true);
    try {
      const result = await docusignApi.createEnvelopeDraft({
        employee_id: employeeId,
        envelope_type: 'employee-offboarding',
        document_ids: documentIds,
      });

      if (result.success) {
        toast.success('Envelope draft created successfully!');
        // Refresh envelope status
        await checkExistingEnvelope();
        setStep1Completed(true); // Mark step 1 as completed
      } else {
        toast.error(`Failed to create envelope: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error creating envelope:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to create envelope: ${errorMessage}`);
    } finally {
      setCreatingEnvelope(false);
    }
  };

  const handleDeleteDraft = async (): Promise<boolean> => {
    if (!employeeId) return false;
    
    if (!window.confirm('Are you sure you want to delete the envelope draft? You will need to create it again if you want to send it.')) {
      return false;
    }

    setCreatingEnvelope(true);
    try {
      const result = await docusignApi.deleteEnvelopeDraft({
        employee_id: employeeId,
        envelope_type: 'employee-offboarding',
      });

      if (result.success) {
        toast.success('Envelope draft deleted successfully');
        setExistingEnvelope(null);
        setStep1Completed(false); // Reset step 1 when draft is deleted
        setStep2Completed(false); // Reset step 2 as well
        return true;
      } else {
        toast.error(`Failed to delete envelope: ${result.error || 'Unknown error'}`);
        return false;
      }
    } catch (error: any) {
      console.error('Error deleting envelope:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to delete envelope: ${errorMessage}`);
      return false;
    } finally {
      setCreatingEnvelope(false);
    }
  };

  const handleResendEnvelope = async () => {
    if (!existingEnvelope || !existingEnvelope.envelope_id) {
      toast.error('No envelope found.');
      return;
    }

    if (existingEnvelope.status !== 'sent') {
      toast.error('Can only resend envelopes that have been sent.');
      return;
    }

    if (!window.confirm('Are you sure you want to resend this envelope? All pending recipients will receive a new notification email.')) {
      return;
    }

    setCreatingEnvelope(true);
    try {
      const result = await docusignApi.resendEnvelope(existingEnvelope.envelope_id);

      if (result.success) {
        toast.success('Envelope resent successfully to all pending recipients.');
        // Optionally refresh envelope status
        await checkExistingEnvelope();
      } else {
        toast.error(`Failed to resend envelope: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error resending envelope:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to resend envelope: ${errorMessage}`);
    } finally {
      setCreatingEnvelope(false);
    }
  };

  const handleVoidEnvelope = async () => {
    if (!existingEnvelope || !existingEnvelope.envelope_id) {
      toast.error('No envelope found.');
      return;
    }

    if (existingEnvelope.status === 'voided') {
      toast.error('Envelope is already voided.');
      return;
    }

    if (!window.confirm('Are you sure you want to void this envelope? This action cannot be undone. You will need to create a new draft envelope.')) {
      return;
    }

    setCreatingEnvelope(true);
    try {
      const result = await docusignApi.voidEnvelope(existingEnvelope.envelope_id);

      if (result.success) {
        toast.success('Envelope voided successfully. You can now create a new draft envelope.');
        // Clear existing envelope so process starts from beginning
        setExistingEnvelope(null);
        setStep1Completed(false);
        setStep2Completed(false);
        // Reset package_sent to false
        handleChange('package_sent', false);
        // Refresh to ensure voided envelope is not shown
        await checkExistingEnvelope();
      } else {
        toast.error(`Failed to void envelope: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error voiding envelope:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to void envelope: ${errorMessage}`);
    } finally {
      setCreatingEnvelope(false);
    }
  };

  const handlePlaceSignatureTabs = async () => {
    if (!existingEnvelope || !existingEnvelope.envelope_id) {
      toast.error('No envelope found. Please create an envelope first.');
      return;
    }

    if (!docusignConnected) {
      toast.error('Please connect to DocuSign first.');
      return;
    }

    setOpeningSenderView(true);
    try {
      const envelopeIdToUse = existingEnvelope.envelope_id;
      const result = await docusignApi.getSenderView(envelopeIdToUse);

      if (result.success && result.url) {
        // Open DocuSign sender view in a new window/tab
        const senderWindow = window.open(result.url, '_blank', 'width=1200,height=800');
        
        if (!senderWindow) {
          toast.info('Please allow pop-ups for this site to open the DocuSign interface.');
          setOpeningSenderView(false);
          return;
        }

        // Listen for when the window closes or user returns
        let windowCheckInterval: NodeJS.Timeout | null = null;
        let timeoutId: NodeJS.Timeout | null = null;
        
        windowCheckInterval = setInterval(() => {
          if (senderWindow.closed) {
            if (windowCheckInterval) {
              clearInterval(windowCheckInterval);
              windowCheckInterval = null;
            }
            if (timeoutId) {
              clearTimeout(timeoutId);
              timeoutId = null;
            }
            // Refresh envelope status to check if tabs were placed or envelope was sent
            setTimeout(async () => {
              // First refresh the envelope to get updated status
              await checkExistingEnvelope();
              // Also explicitly check tabs and DocuSign status
              if (envelopeIdToUse) {
                setCheckingTabs(true);
                try {
                  const tabsResult = await docusignApi.checkEnvelopeTabs(envelopeIdToUse);
                  if (tabsResult.success) {
                    // The backend already updates the status, but we update UI here
                    if (tabsResult.envelope_status === 'tabs_placed') {
                      setStep2Completed(true);
                    } else if (tabsResult.envelope_status === 'sent') {
                      setStep2Completed(true);
                      handleChange('package_sent', true);
                    } else if (tabsResult.has_tabs && tabsResult.envelope_status === 'created') {
                      // Tabs exist but status not updated yet - refresh envelope
                      await checkExistingEnvelope();
                    }
                  }
                } catch (error) {
                  console.error('Error checking envelope tabs:', error);
                } finally {
                  setCheckingTabs(false);
                }
              }
            }, 2000); // Wait 2 seconds for DocuSign to process
          }
        }, 1000);

        // Clear interval after 5 minutes to avoid memory leaks
        timeoutId = setTimeout(() => {
          if (windowCheckInterval) {
            clearInterval(windowCheckInterval);
            windowCheckInterval = null;
          }
        }, 5 * 60 * 1000);

        toast.info('DocuSign interface opened. Place signature tabs on the documents and click Send when ready. The envelope will be sent to the employee.');
      } else {
        toast.error(`Failed to open sender view: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error opening sender view:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to open sender view: ${errorMessage}`);
    } finally {
      setOpeningSenderView(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    onDataChange(updated);
  };

  // Get unique identifier for a document
  const getDocumentId = (doc: PackageDocument): string => {
    return doc.id || `${doc.type}-${doc.name}`;
  };

  // Handle signature checkbox change
  const handleSignatureToggle = (doc: PackageDocument) => {
    const docId = getDocumentId(doc);
    setDocumentsForSigning((prev) => {
      const updated = new Set(prev);
      if (updated.has(docId)) {
        updated.delete(docId);
      } else {
        updated.add(docId);
      }
      return updated;
    });
  };

  // Sync documentsForSigning changes to formData
  useEffect(() => {
    const updatedFormData = {
      ...formDataRef.current,
      documentsForSigning: Array.from(documentsForSigning),
    };
    // Defer callback to avoid calling during render
    const timeoutId = setTimeout(() => {
      onDataChange(updatedFormData);
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [documentsForSigning, onDataChange]);

  // Check if document is selected for signing
  const isDocumentSelectedForSigning = (doc: PackageDocument): boolean => {
    return documentsForSigning.has(getDocumentId(doc));
  };

  if (loading) {
    return (
      <div className="wizard-step">
        <div className="step-header">
          <h4>Document Package</h4>
          <p>Prepare and send the document package for signature</p>
        </div>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!employeeId) {
    return (
      <div className="wizard-step">
        <div className="step-header">
          <h4>Document Package</h4>
          <p>Prepare and send the document package for signature</p>
        </div>
        <div className="step-info-box">
          <i className="bx bx-info-circle"></i>
          <p>Please complete the previous steps first.</p>
        </div>
      </div>
    );
  }

  const allPackageDocuments = [...packageDocuments, ...additionalDocuments];

  return (
    <div className="wizard-step">
      <div className="step-header">
        <h4>Document Package</h4>
        <p>Prepare and send the document package for signature</p>
        <div className="docusign-status-header">
          {docusignLoading ? (
            <span className="docusign-status-loading">
              <i className="bx bx-loader-alt bx-spin"></i>
              Checking DocuSign...
            </span>
          ) : docusignConnected ? (
            <span className="docusign-status-connected">
              <i className="bx bx-check-circle"></i>
              DocuSign: Logged in
            </span>
          ) : (
            <span className="docusign-status-disconnected">
              <i className="bx bx-x-circle"></i>
              DocuSign: Not logged in
            </span>
          )}
        </div>
      </div>

      <div className="step-form">
        {/* Package Documents List */}
        <div className="form-group">
          <label>Package Documents</label>
          {allPackageDocuments.length === 0 ? (
            <div className="step-info-box">
              <i className="bx bx-info-circle"></i>
              <p>No documents in package. Please ensure Termination Letter is uploaded in the Document Generation step.</p>
            </div>
          ) : (
            <div className="document-package-table">
              <div className="document-package-header">
                <div className="header-document">Document</div>
                <div className="header-actions">Actions</div>
              </div>
              <div className="document-package-list">
                {packageDocuments.map((doc, index) => (
                  <div key={index} className="package-document-item">
                    <div className="document-info">
                      <i className="bx bx-file-blank"></i>
                      <div>
                        <strong>{doc.name}</strong>
                        <span className="document-type">Termination Letter</span>
                      </div>
                    </div>
                    <div className="document-actions">
                      <i className="bx bx-check-circle" style={{ color: '#28a745', fontSize: '20px' }}></i>
                    </div>
                  </div>
                ))}
                {additionalDocuments.map((doc) => (
                  <div key={doc.id} className="package-document-item">
                    <div className="document-info">
                      <i className="bx bx-file"></i>
                      <div>
                        <strong>{doc.name}</strong>
                        <span className="document-type">Additional Document</span>
                      </div>
                    </div>
                    <div className="document-actions">
                      <button
                        type="button"
                        className="btn-remove-document"
                        onClick={(e) => {
                          e.preventDefault();
                          handleRemoveDocument(doc);
                        }}
                        disabled={!docusignConnected || docusignLoading}
                      >
                        <i className="bx bx-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Upload Additional Documents */}
        <div className="form-group">
          <label>Add Additional Documents (Optional)</label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className="btn-upload"
            onClick={(e) => {
              e.preventDefault();
              fileInputRef.current?.click();
            }}
            disabled={uploading || !docusignConnected || docusignLoading}
          >
            {uploading ? (
              <>
                <i className="bx bx-loader-alt bx-spin"></i>
                Uploading...
              </>
            ) : (
              <>
                <i className="bx bx-plus"></i>
                Add Document
              </>
            )}
          </button>
          <p className="field-hint">Upload additional documents to include in the package (PDF, DOC, DOCX)</p>
        </div>

        <div className="step-info-box mb-2">
          <i className="bx bx-info-circle"></i>
          <p>
            The document package includes the Termination Letter from the Document Generation step.
            You can add additional documents if needed.
          </p>
        </div>

        {/* DocuSign Process Flow */}
        <div className="form-group">
          <label>DocuSign Process</label>
          {checkingEnvelope && (
            <div className="step-info-box" style={{ background: '#fff3cd', borderLeftColor: '#ffc107', marginBottom: '12px' }}>
              <i className="bx bx-loader-alt bx-spin" style={{ color: '#ffc107' }}></i>
              <div>
                <p style={{ fontWeight: 600, marginBottom: '4px' }}>Checking Envelope Status</p>
                <p style={{ margin: 0 }}>Please wait while we check the current status of your envelope...</p>
              </div>
            </div>
          )}
          {checkingTabs && (
            <div className="step-info-box" style={{ background: '#fff3cd', borderLeftColor: '#ffc107', marginBottom: '12px' }}>
              <i className="bx bx-loader-alt bx-spin" style={{ color: '#ffc107' }}></i>
              <div>
                <p style={{ fontWeight: 600, marginBottom: '4px' }}>Checking Signature Tabs</p>
                <p style={{ margin: 0 }}>Please wait while we check if signature tabs have been placed...</p>
              </div>
            </div>
          )}
          <div className="docusign-process-flow">
            {/* Actions Menu - Top Right */}
            {existingEnvelope && (
              <div className="docusign-actions-menu">
                <button
                  type="button"
                  className="btn-actions-menu"
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  disabled={creatingEnvelope || !docusignConnected || docusignLoading}
                >
                  <i className="bx bx-dots-vertical-rounded"></i>
                </button>
                {showActionsMenu && existingEnvelope.status !== 'sent' && existingEnvelope.status !== 'voided' && existingEnvelope.status !== 'completed' && (
                  <div className="actions-menu-dropdown">
                    <button
                      type="button"
                      className="action-menu-item"
                      onClick={async () => {
                        setShowActionsMenu(false);
                        await handleDeleteDraft();
                      }}
                      disabled={creatingEnvelope}
                    >
                      <i className="bx bx-trash"></i>
                      Delete Draft
                    </button>
                    <button
                      type="button"
                      className="action-menu-item action-menu-void"
                      onClick={async () => {
                        setShowActionsMenu(false);
                        await handleVoidEnvelope();
                      }}
                      disabled={creatingEnvelope}
                    >
                      <i className="bx bx-x-circle"></i>
                      Void
                    </button>
                  </div>
                )}
                {showActionsMenu && existingEnvelope.status === 'sent' && (
                  <div className="actions-menu-dropdown">
                    <button
                      type="button"
                      className="action-menu-item action-menu-resend"
                      onClick={async () => {
                        setShowActionsMenu(false);
                        await handleResendEnvelope();
                      }}
                      disabled={creatingEnvelope}
                    >
                      <i className="bx bx-revision"></i>
                      Resend
                    </button>
                    <button
                      type="button"
                      className="action-menu-item action-menu-void"
                      onClick={async () => {
                        setShowActionsMenu(false);
                        await handleVoidEnvelope();
                      }}
                      disabled={creatingEnvelope}
                    >
                      <i className="bx bx-x-circle"></i>
                      Void
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Step 1: Prepare Envelope / Envelope Created */}
            <div className="process-step">
              <div className={`step-button-wrapper ${step1Completed ? 'completed' : ''}`}>
                {existingEnvelope ? (
                  <button
                    type="button"
                    className="btn-process-step btn-step-created"
                    disabled={true}
                  >
                    <i className="bx bx-check-circle"></i>
                    Envelope Created
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-process-step btn-step-prepare"
                    onClick={handlePrepareEnvelopeClick}
                    disabled={creatingEnvelope || !docusignConnected || docusignLoading || checkingEnvelope || (packageDocuments.length === 0 && additionalDocuments.length === 0)}
                  >
                    {creatingEnvelope || checkingEnvelope ? (
                      <>
                        <i className="bx bx-loader-alt bx-spin"></i>
                        {creatingEnvelope ? 'Creating...' : 'Checking...'}
                      </>
                    ) : (
                      <>
                        <i className="bx bx-envelope"></i>
                        Prepare Envelope
                      </>
                    )}
                  </button>
                )}
                {step1Completed && (
                  <div className="step-checkmark">
                    <i className="bx bx-check-circle"></i>
                  </div>
                )}
              </div>
              <div className="step-label">Step 1: Prepare Envelope</div>
            </div>

            {/* Connector Arrow */}
            <div className={`process-connector ${step1Completed ? 'active' : ''}`}>
              <i className="bx bx-right-arrow-alt"></i>
            </div>

            {/* Step 2: Place Signature Tabs */}
            <div className="process-step">
              <div className={`step-button-wrapper ${step2Completed ? 'completed' : ''}`}>
                <button
                  type="button"
                  className="btn-process-step btn-step-signature"
                  onClick={handlePlaceSignatureTabs}
                  disabled={!step1Completed || !docusignConnected || docusignLoading || openingSenderView || !existingEnvelope || (existingEnvelope?.status !== 'created' && existingEnvelope?.status !== 'tabs_placed')}
                >
                  {openingSenderView ? (
                    <>
                      <i className="bx bx-loader-alt bx-spin"></i>
                      Opening...
                    </>
                  ) : (
                    <>
                      <i className="bx bx-edit"></i>
                      Place Signature Tabs
                    </>
                  )}
                </button>
                {step2Completed && (
                  <div className="step-checkmark">
                    <i className="bx bx-check-circle"></i>
                  </div>
                )}
              </div>
              <div className="step-label">Step 2: Place Signature Tabs</div>
            </div>

            {/* Connector Arrow */}
            <div className={`process-connector ${step2Completed ? 'active' : ''}`}>
              <i className="bx bx-right-arrow-alt"></i>
            </div>

            {/* Step 3: Send Package */}
            <div className="process-step">
              <div className={`step-button-wrapper ${formData.package_sent ? 'completed' : ''}`}>
                <button
                  type="button"
                  className="btn-process-step btn-step-send"
                  onClick={() => {
                    // Placeholder - will implement later
                    console.log('Send package clicked');
                  }}
                  disabled={!step2Completed || !docusignConnected || docusignLoading || formData.package_sent}
                >
                  <i className="bx bx-paper-plane"></i>
                  Send Package
                </button>
                {formData.package_sent && (
                  <div className="step-checkmark">
                    <i className="bx bx-check-circle"></i>
                  </div>
                )}
              </div>
              <div className="step-label">Step 3: Send for Signing</div>
            </div>
          </div>
        </div>

        {/* Package Sent Confirmation */}
        {formData.package_sent && (
          <div className="step-info-box" style={{ background: '#d4edda', borderLeftColor: '#28a745' }}>
            <i className="bx bx-check-circle" style={{ color: '#28a745' }}></i>
            <div>
              <p style={{ fontWeight: 600, marginBottom: '4px' }}>Package Sent Successfully</p>
              <p style={{ margin: 0 }}>
                Document package was sent to {formData.recipient_email}.
              </p>
            </div>
          </div>
        )}

        {/* Overlay when not logged in - covers entire page */}
        {!docusignLoading && !docusignConnected && (
          <div className="docusign-lock-overlay">
            <div className="docusign-lock-content">
              <i className="bx bx-lock-alt" style={{ fontSize: '48px', color: '#6c757d', marginBottom: '16px' }}></i>
              <h4>Login Required</h4>
              <p>You have to login to DocuSign in order to access signing package</p>
              <button
                type="button"
                className="btn-docusign-login-overlay"
                onClick={handleDocuSignLogin}
                disabled={docusignLoading}
              >
                <i className="bx bx-log-in"></i>
                Login to DocuSign
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Envelope Confirmation Modal */}
      {showEnvelopeConfirmModal && (
        <div className="docusign-modal-overlay" onClick={() => setShowEnvelopeConfirmModal(false)}>
          <div className="docusign-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="docusign-modal-header">
              <h3>Confirm Envelope Creation</h3>
              <button
                className="btn-close-modal"
                onClick={() => setShowEnvelopeConfirmModal(false)}
              >
                <i className="bx bx-x"></i>
              </button>
            </div>
            <div className="docusign-modal-body">
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <i className="bx bx-info-circle" style={{ fontSize: '48px', color: '#0dcaf0', marginBottom: '16px' }}></i>
                <h4>Important Notice</h4>
                <p style={{ marginBottom: '24px', lineHeight: '1.6' }}>
                  Once you confirm, you won't be able to change the files in this envelope unless the draft is deleted.
                  <br /><br />
                  If you need to add or remove documents later, you will need to delete this draft and create a new one.
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button
                    type="button"
                    className="btn-docusign-login-overlay"
                    onClick={handleConfirmPrepareEnvelope}
                    disabled={creatingEnvelope}
                  >
                    {creatingEnvelope ? (
                      <>
                        <i className="bx bx-loader-alt bx-spin"></i>
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="bx bx-check"></i>
                        Confirm & Create
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => setShowEnvelopeConfirmModal(false)}
                    style={{
                      padding: '12px 24px',
                      background: '#6c757d',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentPackageStep;
